/* eslint-disable */
import deepEquals from 'fast-deep-equal'
import modules from './modules'
import mutations from './mutations'
import { services } from './services'
import initSync from './sync'
import {
  buildGetCheck, filterExport, getPaginationString, getResults, getServiceArguments,
  handleError, manipulations, pullFromState,
} from './utils'
import {
  capitalize, int, isError, isNumeric, now, reduce, rejection, resolution, stateList, stripFields,
} from '../views/utils'

const CACHE_FETCH_FOR = 15 * 1000

const inject = (mod, manipulationKey) => ({ commit, getters }) => (response) => {
  if (isError(response) || !mod.auto.has('mutations')) return rejection(response)

  const results = getResults(response)

  if (results) {
    manipulations[manipulationKey](mod)(results, { commit, getters })
  }

  return { results }
}

const actions = {
  CLEAR_STATE({ commit }) {
    Object
      .keys(mutations)
      .filter(mutationName => mutationName.startsWith('CLEAR_'))
      .forEach(commit)

    return Promise.resolve()
  },
  SYNC_GLOBAL(store) {
    initSync(modules, store)
  },
}

export const serviceActions = (serviceName) => {
  const service = services[serviceName]
  return {
    CREATE: service.create,
    PATCH: data => service.patch(...getServiceArguments(data)),
    DELETE: data => service.delete(data.id, {}),
    UPDATE: data => service.patch(...getServiceArguments(data)),
  }
}

const setOptimisiticOnlineAction = ({ serviceName, storeKey }, errorHandler) =>
  (reflect, serverSyncAction) => (store, data) => {
    const confirmation = reflect(store)

    // TODO: fix a situation when DELETE is used with a query that needs to be passed through export function
    const exportFn = serverSyncAction === 'DELETE'
      ? null
      : store.getters[`_export${capitalize(storeKey)}`]

    const formattedData = exportFn
      ? filterExport(data, exportFn)
      : data

    return serviceActions(serviceName)[serverSyncAction](formattedData)
      .then((payload) => {
        const results = confirmation(getResults(payload)).results
        return Array.isArray(payload) ? results : results[0]
      })
      .catch(errorHandler(store))
  }

Object.values(modules).forEach((mod) => {
  if (!mod.auto.has('actions')) return

  const service = services[mod.serviceName]
  const addSuffix = str => `${str}_${mod.storeSuffix}`
  const addEntries = inject(mod, 'add')
  const addEntriesDirectly = store => response => addEntries(store, false)(response).results
  const removeEntries = inject(mod, 'remove')
  const errorHandler = handleError(mod)
  const queryFetches = []
  const optimisiticAction = setOptimisiticOnlineAction(mod, errorHandler)

  let append = {
    CREATE: optimisiticAction(addEntries, 'CREATE'),

    FIND_RAW: (store, query) => {
      if (!store.getters.isOnline) return Promise.resolve([])

      return service
        .find(query)
        .then(addEntriesDirectly(store))
        .catch(errorHandler(store))
    },

    FIND: ({ dispatch }, query) =>
      dispatch(addSuffix('FIND_RAW'), query)
        .then(response => resolution(getResults(response))),

    GET: (store, id) => {
      if (!store.getters.isOnline) return Promise.resolve()

      return service
        .get(id)
        .then(addEntriesDirectly(store))
        .catch(errorHandler(store))
    },

    // similar to GET but with checking if it's already fetched & pagination
    FETCH: (context, query = {}) => {
      const { commit, dispatch, state } = context
      const modState = state[mod.storeKey]
      const list = stateList(modState)
      const getCheck = idOrQuery => buildGetCheck(mod, list, context)(idOrQuery)

      // FIND of many entries by ID
      if (Array.isArray(query)) {
        const { exist, fetch } = getCheck(query.filter(isNumeric))

        return (fetch.length
          ? dispatch(addSuffix('FIND'), {
            id: {
              $in: fetch,
            },
          })
          : resolution([])
        ).then(fetched => [...fetched, ...pullFromState(list, exist)])
      }

      // GET of one entry by ID
      if (isNumeric(query)) {
        const id = int(query)
        return getCheck(id).exist.length
          ? resolution(list[id])
          : dispatch(addSuffix('GET'), id)
      }

      // FIND by query
      // TODO: deprecate lastFullFetch since we shouldn't use full fetches
      let doFetch = !modState.lastFullFetch
      if (!doFetch) return resolution(Object.values(list))

      if (!Object.keys(query).length) {
        commit(addSuffix('SET_LAST_FETCH'), now())
      }

      const refetchTime = now() - (mod.cacheFor || CACHE_FETCH_FOR)
      const { $page, ...serverQuery } = query
      const queryWithoutPagination = stripFields(serverQuery, ['$skip'])
      let paginationString = getPaginationString(serverQuery)
      let pageNumber = 0
      let recordedFetch = queryFetches.find(recordedFetch =>
        deepEquals(recordedFetch.query, queryWithoutPagination))

      if (recordedFetch) {
        // if has pagination
        if ($page) {
          const previousPageNumber = recordedFetch.pagination.page

          // support relative string $page such as '+1' and '-2'
          pageNumber = typeof $page === 'number'
            ? $page
            : previousPageNumber + int($page.replace('+', ''))

          serverQuery.$skip = recordedFetch.pagination.limit * pageNumber
        }

        paginationString = getPaginationString(serverQuery)

        if (recordedFetch.fetchedAt[paginationString] > refetchTime) {
          return Promise.resolve([])
        }

        recordedFetch.fetchedAt[paginationString] = now()
      } else {
        // remember as a fetched query
        queryFetches.push({
          query: queryWithoutPagination,
          fetchedAt: {
            [paginationString]: now(),
          },
          pagination: {
            limit: query.$limit || 20,
            skip: query.$skip || 0,
            page: pageNumber,
          },
        })

        recordedFetch = queryFetches[queryFetches.length - 1]
      }

      return dispatch(addSuffix('FIND_RAW'), serverQuery)
      // .then(({ data, ...pagination }) => {
      //   recordedFetch.pagination = {
      //     ...pagination,
      //     page: pageNumber,
      //   }

      //   return Promise.resolve(data)
      // })
    },

    DELETE: optimisiticAction(removeEntries, 'DELETE'),
    UPDATE: optimisiticAction(addEntries, 'UPDATE'),
    PATCH: optimisiticAction(addEntries, 'PATCH'),
  }

  Object.assign(actions, reduce(append, (agg, action, name) => ({
    ...agg,
    [addSuffix(name)]: action,
  })))
})

export default actions



// WEBPACK FOOTER //
// ./src/store/actions.js